// convex/lib/config/domains/configuration.ts

import { validatorCorrelative } from "../../../companies/configuration/correlatives";
import { validatorDocumentType } from "../../../companies/configuration/documents_types";
import { validatorEmissionPoint } from "../../../companies/configuration/emission_points";
import { validatorNomenclatureConfig } from "../../../companies/configuration/nomenclatures";
import { validatorUsers } from "../../../system/users";
import {
  validatorCustomField,
  validatorNativeFieldSetting,
} from "../../../companies/custom_fields";
import { validatorScript } from "../../../companies/scripts";
import {
  validatorPdfTemplate,
  validatorPdfTemplateAsset,
  validatorPdfTemplateVersion,
} from "../../../companies/pdf_templates";
import { validatorEmailTemplate, validatorEmailTemplateVersion } from "../../../companies/email_templates";
import type { DomainGroup, TableScope } from "../types";

// ============================================================================
// CONFIGURATION DOMAIN - Status Values for i18n
// ============================================================================

export const CORRELATIVE_STATUSES = ["active", "inactive"] as const;
export const DOCUMENT_TYPE_STATUSES = ["active", "inactive"] as const;

export const configurationDomain: DomainGroup = {
  id: "configuration",
  name: "Configuración de Negocio",
  description:
    "Configuración de correlativos, tipos de documento y puntos de emisión",
  icon: "Settings",
  order: 23,
  status: "active",

  // ── Configuración i18n del dominio ────────────────────
  i18n: {
    namespace: "configuration",
    keys: {
      title: "configuration.title",
      description: "configuration.description",
    },
  },

  modules: [
    {
      id: "correlatives",
      name: "Correlativos",
      description: "Gestión de secuencias y correlativos de documentos",
      icon: "FileDigit",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/correlatives"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 3,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "configuration.correlatives",
        keys: {
          title: "configuration.correlatives.title",
          description: "configuration.correlatives.description",
          longDescription: "configuration.correlatives.longDescription",
          searchPlaceholder: "configuration.correlatives.searchPlaceholder",
          emptyState: "configuration.correlatives.emptyState",
          actions: {
            create: "configuration.correlatives.actions.create",
            edit: "configuration.correlatives.actions.edit",
            delete: "configuration.correlatives.actions.delete",
            view: "configuration.correlatives.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [{ field: "status", values: CORRELATIVE_STATUSES }],
      },

      tables: [
        {
          tableName: "correlatives",
          schema: validatorCorrelative,
          scope: "branch" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "documentType",
              schema: validatorDocumentType,
              tableName: "documents_types",
              sourceField: "documentTypeId",
            },
          ],
          searchFields: ["name"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [{ tableName: "documents_types" }],
    },
    {
      id: "nomenclature",
      name: "Nomenclaturas",
      description: "Codigos y nomenclaturas automaticas por tabla",
      icon: "Hash",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/nomenclatures"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 4,
        status: "active",
      },
      i18n: {
        namespace: "system.nomenclature",
        keys: {
          title: "system.nomenclature.title",
          description: "system.nomenclature.description",
          longDescription: "system.nomenclature.longDescription",
          searchPlaceholder: "system.nomenclature.searchPlaceholder",
          emptyState: "system.nomenclature.emptyState",
          actions: {
            create: "system.nomenclature.actions.create",
            edit: "system.nomenclature.actions.edit",
            delete: "system.nomenclature.actions.delete",
            view: "system.nomenclature.actions.view",
          },
        },
        translatableFields: [],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "nomenclature",
          schema: validatorNomenclatureConfig,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "lastCreatedBy",
              sourceField: "internalControl.createdBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "prefix"],
          hasStatusIndex: true,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "documents_types",
      name: "Tipos de Documento",
      description:
        "Configuración de tipos de documento (facturas, recibos, etc.)",
      icon: "FileType",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 6,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "configuration.documents_types",
        keys: {
          title: "configuration.documents_types.title",
          description: "configuration.documents_types.description",
          longDescription: "configuration.documents_types.longDescription",
          searchPlaceholder: "configuration.documents_types.searchPlaceholder",
          emptyState: "configuration.documents_types.emptyState",
          actions: {
            create: "configuration.documents_types.actions.create",
            edit: "configuration.documents_types.actions.edit",
            delete: "configuration.documents_types.actions.delete",
            view: "configuration.documents_types.actions.view",
          },
        },
        translatableFields: ["name", "description"],
        translatableEnums: [
          { field: "status", values: DOCUMENT_TYPE_STATUSES },
        ],
      },

      tables: [
        {
          tableName: "documents_types",
          schema: validatorDocumentType,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "code"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "emission_points",
      name: "Puntos de Emisión",
      description: "Configuración de puntos de emisión para facturas",
      icon: "FileInput",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: [],
      navigation: {
        showInNav: false,
        contexts: [],
        navOrder: 7,
        status: "active",
      },

      // ── Configuración i18n del módulo ────────────────────
      i18n: {
        namespace: "configuration.emission_points",
        keys: {
          title: "configuration.emission_points.title",
          description: "configuration.emission_points.description",
          longDescription: "configuration.emission_points.longDescription",
          searchPlaceholder: "configuration.emission_points.searchPlaceholder",
          emptyState: "configuration.emission_points.emptyState",
          actions: {
            create: "configuration.emission_points.actions.create",
            edit: "configuration.emission_points.actions.edit",
            delete: "configuration.emission_points.actions.delete",
            view: "configuration.emission_points.actions.view",
          },
        },
        translatableFields: ["name"],
        translatableEnums: [],
      },

      tables: [
        {
          tableName: "emission_points",
          schema: validatorEmissionPoint,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["code", "name"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      // Colecciones: campos personalizados tipados sobre tablas existentes
      // (por ahora clientes). Cubre dos rutas hermanas bajo `/settings/`:
      // `/settings/collections` (editor de campos personalizados) y
      // `/settings/catalogs` (vista agregada de catálogos: marcas,
      // categorías, etiquetas…). Cada catálogo adentro trae su propio
      // módulo de permisos. Ver convex/companies/custom_fields.
      id: "custom_fields",
      name: "Colecciones",
      description:
        "Campos personalizados para las tablas del sistema y catálogos",
      icon: "Database",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/collections", "/settings/catalogs", "/settings/pdf-templates", "/settings/schema"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 5,
        status: "active",
      },
      i18n: {
        namespace: "configuration.custom_fields",
        keys: {
          title: "configuration.custom_fields.title",
          description: "configuration.custom_fields.description",
          longDescription: "configuration.custom_fields.longDescription",
          searchPlaceholder: "configuration.custom_fields.searchPlaceholder",
          emptyState: "configuration.custom_fields.emptyState",
          actions: {
            create: "configuration.custom_fields.actions.create",
            edit: "configuration.custom_fields.actions.edit",
            delete: "configuration.custom_fields.actions.delete",
            view: "configuration.custom_fields.actions.view",
          },
        },
        translatableFields: ["label"],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "custom_fields",
          schema: validatorCustomField,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["label", "key"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Ocultar / obligatorio de campos nativos (hereda el permiso del
          // módulo custom_fields por la expansión de tablas del módulo).
          tableName: "native_field_settings",
          schema: validatorNativeFieldSetting,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["fieldKey"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Plantillas PDF del motor react-pdf (sistema y colecciones).
          tableName: "pdf_templates",
          schema: validatorPdfTemplate,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Historial inmutable de cada plantilla (restaurar versiones).
          tableName: "pdf_template_versions",
          schema: validatorPdfTemplateVersion,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Plantillas de correo (editor de React Email).
          tableName: "email_templates",
          schema: validatorEmailTemplate,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Historial inmutable de cada plantilla de correo.
          tableName: "email_template_versions",
          schema: validatorEmailTemplateVersion,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
        {
          // Biblioteca de imagenes de las plantillas (logos, membretes).
          tableName: "pdf_template_assets",
          schema: validatorPdfTemplateAsset,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
    {
      // Sistema de Scripts: alternativa standalone a function fields +
      // triggers embebidos. Cada script es un artefacto con codigo +
      // metadata que se evalua contra el sandbox de function fields.
      // Vive bajo `/settings/scripts` (lista) + `/settings/scripts/$id`
      // (editor + consola de runs).
      id: "scripts",
      name: "Scripts",
      description:
        "Codigo reutilizable disparado por eventos sobre records o manualmente",
      icon: "FileCode",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/scripts"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 8,
        status: "active",
      },
      i18n: {
        namespace: "configuration.scripts",
        keys: {
          title: "configuration.scripts.title",
          description: "configuration.scripts.description",
          longDescription: "configuration.scripts.longDescription",
          searchPlaceholder: "configuration.scripts.searchPlaceholder",
          emptyState: "configuration.scripts.emptyState",
          actions: {
            create: "configuration.scripts.actions.create",
            edit: "configuration.scripts.actions.edit",
            delete: "configuration.scripts.actions.delete",
            view: "configuration.scripts.actions.view",
          },
        },
        translatableFields: ["label"],
        translatableEnums: [],
      },
      tables: [
        {
          tableName: "scripts",
          schema: validatorScript,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name"],
          hasStatusIndex: false,
          customRules: [],
        },
      ],
      readOnlyTables: [],
    },
  ],
};
